// Employee Delivery section — dashboard parity v3.5.
//
// Mirrors v1's "Delivery Performance" chart: a centered horizontal
// bar per staffer showing average days ahead/behind their due
// dates across done Commitments in the active window. Negative
// (left of zero) means ahead of schedule; positive (right) means
// behind. Sits in the Employee Analytics group between Blocking
// work and Most Overdue.

import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

import {
  DashboardCard,
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
  useCommonFilters,
  useEmployeeDeliveryQuery,
} from "@/features/dashboard";


export const EMPLOYEE_DELIVERY_SECTION = "employee-delivery" as const;


const VISIBLE_LIMIT = 50;
const PAGE_SIZE = 6;


export function EmployeeDeliverySection() {
  const filters = useCommonFilters();
  const query = useEmployeeDeliveryQuery(filters);

  const rows = (query.data?.rows ?? []).slice(0, VISIBLE_LIMIT);

  // Symmetric scale around zero so "ahead" and "behind" bars
  // anchor against a centred axis. Computed across the full row set
  // (not just the visible page) so the scale doesn't jump while
  // paginating.
  const absMax = rows.reduce(
    (m, r) => Math.max(m, Math.abs(r.average_days_offset)),
    1,
  );

  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * PAGE_SIZE;
  const visible = rows.slice(start, start + PAGE_SIZE);
  const showPager = rows.length > PAGE_SIZE;

  return (
    <DashboardCard
      section={EMPLOYEE_DELIVERY_SECTION}
      title="Delivery performance"
      subtitle="Average days early (left, sage) / late (right, ember) per staffer in the active window."
    >
      {query.isLoading ? (
        <DashboardLoadingState variant="card" rows={4} />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load delivery performance"
          retry={() => query.refetch()}
        />
      ) : rows.length === 0 ? (
        <DashboardEmptyState
          title="No completed Commitments with due dates in this window"
          description="As Commitments close out against their due dates, this chart fills in."
        />
      ) : (
        <ul role="list" className="flex flex-col gap-2">
          {visible.map((row) => {
            const offset = row.average_days_offset;
            const ahead = offset < 0;
            const widthPct = Math.min(
              50, Math.max(2, (Math.abs(offset) / absMax) * 50),
            );
            return (
              <li
                key={row.user_id}
                data-testid={`employee-delivery-row-${row.user_id}`}
                className="grid grid-cols-[160px_1fr_70px] items-center gap-3"
              >
                <Link
                  to={row.drill_through_url}
                  className="truncate text-xs font-medium text-text-primary hover:underline"
                  title={row.display_name}
                >
                  {row.display_name}
                </Link>
                <div className="relative h-3 rounded-full bg-recessed/40">
                  {/* Centre tick */}
                  <span
                    aria-hidden="true"
                    className="absolute left-1/2 top-0 h-full w-px bg-border-subtle"
                  />
                  <div
                    className={[
                      "absolute top-0 h-full",
                      ahead
                        ? "right-1/2 rounded-l-full bg-sage"
                        : "left-1/2 rounded-r-full bg-ember",
                    ].join(" ")}
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
                <span
                  data-testid={`employee-delivery-offset-${row.user_id}`}
                  className={[
                    "text-right text-[11px] font-semibold tabular-nums",
                    ahead ? "text-teal" : "text-ember-ink",
                  ].join(" ")}
                  title={`${row.completed_count} completed`}
                >
                  {ahead ? "" : "+"}
                  {offset.toFixed(1)}d
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {showPager && (
        <footer
          data-testid="employee-delivery-pager"
          className="flex items-center justify-between text-[11px] text-text-tertiary"
        >
          <span>
            {start + 1}–{start + visible.length} of {rows.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid="employee-delivery-pager-prev"
              aria-label="Previous staff"
              disabled={safePage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="inline-flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-recessed disabled:cursor-not-allowed disabled:text-text-tertiary"
            >
              <ChevronLeft size={14} aria-hidden="true" />
            </button>
            <span className="tabular-nums">
              {safePage + 1} / {pageCount}
            </span>
            <button
              type="button"
              data-testid="employee-delivery-pager-next"
              aria-label="Next staff"
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              className="inline-flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-recessed disabled:cursor-not-allowed disabled:text-text-tertiary"
            >
              <ChevronRight size={14} aria-hidden="true" />
            </button>
          </div>
        </footer>
      )}
    </DashboardCard>
  );
}
